import { illustrations } from '../data/illustrations.js';

/**
 * Initialisation de l'animation p5.js
 * @param {HTMLElement} containerNode - Le conteneur DOM cible (ex: div React)
 */
export function initP5Drawings(containerNode) {
  
  // Configuration du sketch en mode instance pour isoler p5.js
  const sketch = (p) => {
    
    /**
     * Variables d'état et configuration
     */
    let historiqueImages = [];
    let dernierDepotX = 0;
    let dernierDepotY = 0;
    let indexPhotoActuelle = 0;
    let photosChargees = [];
    const NOMBRE_TOTAL_PHOTOS = illustrations.length;
    const imagesRequises = new Set();

    function chargerSiNecessaire(index) {
      if (imagesRequises.has(index)) return;
      imagesRequises.add(index);
      p.loadImage(illustrations[index].src, (img) => {
        photosChargees[index] = img;
      });
    }
    
    // Distance minimale requise entre deux affichages d'images
    let distanceMinimumEntrePhotos = window.innerWidth < 600 ? 100 : 180;

    /**
     * Calcule les dimensions du canvas en fonction de la taille de l'écran
     */
    function calculerTailleCanvas() {
      if (window.innerWidth < 932) {
        let largeurMobile = window.innerWidth - 40;
        return { largeur: largeurMobile, hauteur: largeurMobile * 1.8 }; 
      }
      return { largeur: 700, hauteur: 700 };
    }

    p.setup = () => {
      const dimensions = calculerTailleCanvas();
      p.createCanvas(dimensions.largeur, dimensions.hauteur);

      // Pré-allocation du tableau d'images
      for (let i = 0; i < NOMBRE_TOTAL_PHOTOS; i++) {
        photosChargees[i] = null;
      }

      // Chargement des 4 premières images uniquement (les autres se chargent à la demande)
      for (let i = 0; i < Math.min(4, NOMBRE_TOTAL_PHOTOS); i++) {
        chargerSiNecessaire(i);
      }
    };

    p.draw = () => {
      p.background("#6c6764");

      // Calcul de la distance depuis la dernière position enregistrée
      let distanceParcourue = p.dist(p.mouseX, p.mouseY, dernierDepotX, dernierDepotY);
      
      // Vérification des limites du canvas
      let sourisDansCanvas = p.mouseX > 0 && p.mouseX < p.width && p.mouseY > 0 && p.mouseY < p.height;

      // Condition de déclenchement pour une nouvelle image
      if (distanceParcourue > distanceMinimumEntrePhotos && sourisDansCanvas) {
        
        // Vérification du chargement effectif de l'image
        if (photosChargees[indexPhotoActuelle]) {
          
          // Enregistrement des propriétés de l'image à afficher
          historiqueImages.unshift({
            x: p.mouseX,
            y: p.mouseY,
            idPhoto: indexPhotoActuelle,
            angleRotation: p.random(-0.06, 0.06),
            vitesseX: p.random(-0.15, 0.15),
            vitesseY: p.random(-0.4, -0.2) 
          });
        }

        // Limitation du nombre d'images affichées simultanément
        if (historiqueImages.length > 10) {
            historiqueImages.pop();
        }

        // Mise à jour des coordonnées de référence
        dernierDepotX = p.mouseX;
        dernierDepotY = p.mouseY;
        
        // Incrémentation cyclique de l'index de l'image
        indexPhotoActuelle = (indexPhotoActuelle + 1) % NOMBRE_TOTAL_PHOTOS;

        // Préchargement des 3 prochaines images à la demande
        for (let j = 1; j <= 3; j++) {
          chargerSiNecessaire((indexPhotoActuelle + j) % NOMBRE_TOTAL_PHOTOS);
        }
      }

      // Boucle de rendu (itération inversée pour gérer la superposition visuelle)
      for (let i = historiqueImages.length - 1; i >= 0; i--) {
        let photoEnCours = historiqueImages[i];
        let imageSource = photosChargees[photoEnCours.idPhoto];

        // Ignorer le rendu si le fichier n'est pas encore disponible
        if (!imageSource) continue; 

        // Mise à jour de la position (effet de dérive)
        photoEnCours.x += photoEnCours.vitesseX;
        photoEnCours.y += photoEnCours.vitesseY;

        // Calcul de l'opacité (fondu basé sur l'ancienneté dans le tableau)
        let anciennete = i / historiqueImages.length; 
        let opacite = 255 * (1 - Math.pow(anciennete, 2));
        
        // Calcul des dimensions d'affichage adaptatives
        let tailleRelative = p.width < 700 ? p.width * 0.55 : p.width * 0.35;
        let ratioAspect = imageSource.width / imageSource.height;
        let largeurAffichee = ratioAspect > 1 ? tailleRelative : tailleRelative * ratioAspect;
        let hauteurAffichee = ratioAspect > 1 ? tailleRelative / ratioAspect : tailleRelative;

        // Rendu graphique
        p.push();
        p.translate(photoEnCours.x, photoEnCours.y);
        p.rotate(photoEnCours.angleRotation);
        
        // Fond blanc (style cadre polaroid)
        p.rectMode(p.CENTER);
        p.fill(255, opacite);
        p.noStroke();
        let epaisseurCadre = 6;
        p.rect(0, 0, largeurAffichee + epaisseurCadre * 2, hauteurAffichee + epaisseurCadre * 2, 2);
        
        // Texture de l'image
        p.tint(255, opacite);
        p.imageMode(p.CENTER);
        p.image(imageSource, 0, 0, largeurAffichee, hauteurAffichee);
        
        p.pop();
      }
    };

    // Ajustement des dimensions lors du redimensionnement de la fenêtre
    p.windowResized = () => {
      const dimensions = calculerTailleCanvas();
      p.resizeCanvas(dimensions.largeur, dimensions.hauteur);
    };
  };

  // Instanciation de p5.js
  const p5Instance = new window.p5(sketch, containerNode);

  // Fonction de nettoyage pour le démontage du composant (évite les fuites de mémoire)
  return () => {
    p5Instance.remove();
  };
}